import alt from 'components/Dispatcher';
import MailActions from 'actions/inbox/MailActions';
import InboxItem, {InboxItemId} from 'records/InboxItem';
import moment from 'moment';
import PaginatedResults from 'records/PaginatedResults';
import {Map, Set} from 'immutable';
import {groupItems} from 'utils/transforms/groupItems';
import {Store} from '@store/@types/alt/Store';
import InboxFilters from 'records/InboxFilters';

interface MailStoreState {
  // mail ids mapped based on what filters are applied
  mailByFilters: Map<InboxFilters, PaginatedResults>;
  // all (grouped) mail mapped by id
  mail: Map<InboxItemId, InboxItem>;
  // all (flattened) mail that came from the server
  // use me when you need to get an item of mail by id
  ungroupedMail: Map<InboxItemId, InboxItem>;
  // items which have the reverse of areAllSelected
  reverseSelectedMail: Set<InboxItemId>;
  areAllSelected: boolean;
  isLoading: boolean;
  wasError: boolean;
}

class MailStore extends Store<MailStoreState> {
  constructor() {
    super();
    this.state = {
      // mail ids mapped based on what filters are applied
      // InboxItems here don't use the InboxItem class! no idea why, too scared to try to fix it
      mailByFilters: Map(),
      // all (grouped) mail mapped by id
      mail: Map(),
      // all (flattened) mail that came from the server
      // use me when you need to get an item of mail by id
      ungroupedMail: Map(),
      // items which have the reverse of areAllSelected
      reverseSelectedMail: Set(),
      areAllSelected: false,
      isLoading: false,
      wasError: false,
    };
    this.bindActions(MailActions);
  }

  clearMailForFilters(filters) {
    this.setState({
      mailByFilters: this.state.mailByFilters.delete(filters),
      reverseSelectedMail: Set(),
      areAllSelected: false,
      wasError: false,
    });
  }

  fetchMail() {
    this.setState({isLoading: true, wasError: false});
  }

  fetchMailSuccess([filters, data]) {
    const {mailByFilters} = this.state;
    const mailResults = mailByFilters.get(filters) || new PaginatedResults();

    // group items according to a few rules - see groupItems.js
    const grouped = groupItems(data);

    this.setState({
      isLoading: false,
      mail: this.state.mail.merge(Map(grouped.items.map(item => [item.id, new InboxItem(item)]))),
      ungroupedMail: this.state.ungroupedMail.merge(
        Map(data.items.map(item => [item.id, new InboxItem(item)]))
      ),
      mailByFilters: mailByFilters.set(
        filters,
        mailResults.merge({
          items: mailResults.items.concat(grouped.items.map(item => item.id)),
          page: data.options.page,
          numPages: data.options.numPages,
        })
      ),
    });
  }

  fetchMailError(folderId) {
    this.setState({isLoading: false, wasError: true});
  }

  fetchMailItemSuccess([item, overwrite]) {
    const existing = this.state.mail.get(item.id);

    if (!existing || overwrite) {
      this.setState({
        mail: this.state.mail.set(item.id, item),
      });
    }
  }

  bulkMoveFolderSuccess({filters, ids, to}) {
    const itemIds = filters ? this.state.mailByFilters.getIn([filters, 'items']) : ids;
    const mail = this.state.mail.mapEntries(([id, item]) => [
      id,
      itemIds.includes(item.id) ? item.set('folderId', to) : item,
    ]);
    this.setState({
      mail,
      mailByFilters: this.refreshMailByFiltersFor(mail, !!filters),
    });
  }

  bulkMarkAsReadSuccess({filters, ids}) {
    const itemIds = filters ? this.state.mailByFilters.getIn([filters, 'items']) : ids;
    const mail = this.state.mail.mapEntries(([id, item]) => [
      id,
      itemIds.includes(item.id) ? item.set('viewedDate', moment()) : item,
    ]);
    this.setState({
      mail,
      mailByFilters: this.refreshMailByFiltersFor(mail),
    });
  }

  bulkSetPinnedSuccess({filters, ids, to, pinned}) {
    const mail = this.state.mail.mapEntries(([id, item]) => [
      id,
      filters || ids.includes(item.id) ? item.set('pinned', pinned) : item,
    ]);
    this.setState({
      mail,
      mailByFilters: this.refreshMailByFiltersFor(mail),
    });
  }

  saveMailSuccess(item) {
    const mail = this.state.mail.set(item.id, item);
    this.setState({
      mail,
      mailByFilters: this.refreshMailByFiltersFor(mail),
    });
  }

  // In case changes made to the item affects which filters it shows up
  // in, we need to update the filter results.  Consider reevaluating this
  // strategy as it adds redundant code to the FE that is in the BE
  // (see MailItem.isMatch) and it also complicates the logic.. although
  // figuring how to efficiently reload the data without it being jarring
  // to the user Would also be (posibily even more) complicated
  /**
   * @param mail the fetched items across all tags
   * @param noMore indicate if there can be no more items (satisfying this filter)
   * @returns {Iterable.Keyed<any, any>} the new mailByFilters part of the state
   */
  refreshMailByFiltersFor(mail, noMore = false) {
    const cachedItems = mail.valueSeq();
    return this.state.mailByFilters.mapEntries(
      ([filters, searchResults = new PaginatedResults()]) => [
        filters,
        searchResults
          .set(
            'items',
            cachedItems
              .filter(item => filters.isMatch(item))
              .sortBy(
                item => item.created,
                (a, b) => (a.isAfter(b) ? -1 : 1)
              )
              .map(item => item.id)
          )
          .set('numPages', noMore ? 0 : searchResults.get('numPages')),
      ]
    );
  }

  selectMail(item) {
    this.setState({
      reverseSelectedMail: this.state.reverseSelectedMail[
        this.state.areAllSelected ? 'delete' : 'add'
      ](item.id),
    });
  }

  unselectMail(item) {
    this.setState({
      reverseSelectedMail: this.state.reverseSelectedMail[
        this.state.areAllSelected ? 'add' : 'delete'
      ](item.id),
    });
  }

  selectAll() {
    this.setState({areAllSelected: true});
  }

  unselectAll() {
    this.setState({areAllSelected: false, reverseSelectedMail: Set()});
  }

  markCallAsReadError(e) {
    console.error(e);
  }

  markCallAsReadSuccess(callId) {
    // Mark items with this callId as viewed.
    // We don't want to mess up the table by refetching items entirely.
    const now = moment();
    const ungroupedMail = this.state.ungroupedMail.map(item =>
      item.call.id === callId ? item.set('viewedDate', now) : item
    );
    const mail = this.state.mail.map(item =>
      item.call.id === callId ? item.set('viewedDate', now) : item
    );

    this.setState({
      mail,
      ungroupedMail,
    });
  }
}

export default alt.createStore(MailStore);
